import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MailWarning, X, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DISMISS_KEY = 'unprocessed_alerts_dismissed';

export const UnprocessedEmailsAlert = () => {
    const { getAuthHeaders } = useAuth();
    const navigate = useNavigate();
    const [alerts, setAlerts] = useState([]);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        axios.get(`${API}/api/admin/unprocessed-alerts`, { headers: getAuthHeaders() })
            .then(res => {
                const list = res.data.alerts || [];
                setAlerts(list);
                const sig = list.map(a => a.id).sort().join('||');
                setDismissed(sig !== '' && localStorage.getItem(DISMISS_KEY) === sig);
            })
            .catch(err => console.error('Error fetching unprocessed alerts:', err));
    }, [getAuthHeaders]);

    if (alerts.length === 0 || dismissed) return null;

    const dismiss = () => {
        localStorage.setItem(DISMISS_KEY, alerts.map(a => a.id).sort().join('||'));
        setDismissed(true);
    };

    return (
        <div data-testid="unprocessed-emails-alert" className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 shadow-sm">
            <div className="flex items-start gap-3">
                <div className="bg-amber-500 rounded-full p-2 shrink-0">
                    <MailWarning className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-amber-900 text-base md:text-lg" data-testid="unprocessed-alert-title">
                        {alerts.length} correo{alerts.length !== 1 ? 's' : ''} de pago sin procesar
                    </h3>
                    <p className="text-sm text-amber-800 mt-0.5">
                        Stripe/PayPal envió notificaciones de pago que el sistema no pudo leer. No están en la contabilidad.
                    </p>
                    <ul className="mt-3 space-y-1.5">
                        {alerts.slice(0, 5).map((a, i) => (
                            <li key={a.id} className="text-sm text-amber-900 flex flex-wrap items-center gap-x-2" data-testid={`unprocessed-alert-item-${i}`}>
                                <span className="px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-xs font-semibold">{a.bank}</span>
                                <span className="font-medium truncate max-w-full">{a.subject}</span>
                                <span className="text-xs text-amber-700">{a.date_display}</span>
                            </li>
                        ))}
                        {alerts.length > 5 && (
                            <li className="text-xs text-amber-700">y {alerts.length - 5} más…</li>
                        )}
                    </ul>
                    <Button
                        size="sm"
                        className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => navigate('/dashboard/daily-deposits?tab=failed')}
                        data-testid="unprocessed-alert-view-btn"
                    >
                        Ver emails no procesados <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Button>
                </div>
                <button
                    onClick={dismiss}
                    className="text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded-full p-1 transition-colors shrink-0"
                    aria-label="Descartar"
                    data-testid="unprocessed-alert-dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};
